import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildService, buildBreadcrumb } from "@/lib/seo";

const ROLES = [
  { id: "realtor", title: "Realtors / Agents", body: "Get matched with sellers who want to list — including motivated owners and probate/inherited situations." },
  { id: "attorney", title: "Attorneys", body: "Probate, foreclosure defense, divorce, tax delinquency. Sellers actively need legal guidance." },
  { id: "lender", title: "Lenders / Funding Partners", body: "Refi, hard money, loan mod referrals. Connect with homeowners actively shopping for solutions." },
  { id: "contractor", title: "Contractors", body: "Distressed properties need repair estimates. Investors hire fast." },
  { id: "title", title: "Title Companies", body: "Be the preferred title partner inside our marketplace." },
];

export default function ForProfessionals() {
  return (
    <div>
      <Seo
        title="For Realtors, Attorneys, Lenders, Contractors & Title Companies"
        description="Become a verified ADN provider and get in front of distressed sellers actively seeking help. Listing, legal, lending, repair, and closing professionals welcome."
        path="/for-professionals"
        jsonLd={[
          buildService(
            "Verified provider network for distressed real estate",
            "Realtors, attorneys, lenders, contractors, and title companies serving distressed-property sellers.",
          ),
          buildBreadcrumb([
            { name: "Home", url: "https://adn-marketplace.com/" },
            { name: "For Professionals", url: "https://adn-marketplace.com/for-professionals" },
          ]),
        ]}
      />
      <Navbar />
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <p className="eyebrow text-[var(--adn-gold)]">For professionals</p>
          <h1 className="font-display text-5xl sm:text-6xl font-light mt-3 max-w-3xl">Become a verified ADN provider.</h1>
          <p className="mt-6 text-white/75 max-w-2xl text-lg leading-relaxed">
            Realtors, attorneys, lenders, contractors, and title companies — get in front of distressed sellers actively seeking help.
          </p>
          <div className="mt-10 flex gap-3">
            <Link to="/login" data-testid="fp-join" className="btn-gold px-7 py-4 rounded text-sm">Apply To Join</Link>
            <Link to="/pricing" className="btn-outline-light px-7 py-4 rounded text-sm">Pricing</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-6">
        {ROLES.map((r) => (
          <div key={r.id} className="bg-white border border-[var(--adn-line)] p-8">
            <h3 className="font-display text-2xl font-semibold">{r.title}</h3>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">{r.body}</p>
          </div>
        ))}
      </section>

      <section className="bg-white border-y border-[var(--adn-line)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20">
          <p className="eyebrow">Why pros choose ADN</p>
          <h2 className="font-display text-4xl font-medium mt-3 mb-8">Distressed-property clients, organized.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="font-display text-lg font-semibold">Pre-qualified intent</p>
              <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Sellers come to ADN actively looking for solutions — not cold prospects. Foreclosure, probate, divorce, and inheritance situations all need professional guidance.</p>
            </div>
            <div>
              <p className="font-display text-lg font-semibold">Match to your specialty</p>
              <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Probate attorneys see probate cases. Loan-mod lenders see foreclosure cases. Contractors see properties needing repair. We route by situation tags so you only see relevant leads.</p>
            </div>
            <div>
              <p className="font-display text-lg font-semibold">Built-in trust</p>
              <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Verified ADN badge, license-number capture, and admin review give your clients confidence before they ever speak with you.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20">
        <p className="eyebrow">Frequently asked</p>
        <h2 className="font-display text-4xl font-medium mt-3 mb-10">Provider FAQ.</h2>
        <div className="space-y-7">
          {[
            { q: "How does approval work?", a: "After you sign up and choose your role, ADN staff reviews your profile, license number, and any supporting documents you upload. Once approved, your verified badge appears on every offer or message you send." },
            { q: "What if I serve multiple roles (e.g., Realtor + investor)?", a: "Pick the primary role at signup. You can add additional capabilities later via your profile. Admin can promote your account if needed." },
            { q: "Can I bring my own clients to the platform?", a: "Yes. You can invite sellers you already know to submit through ADN so you can document offers and use the marketplace tooling — even if they are not actively shopping providers." },
            { q: "Is there a cap on how many leads I can work?", a: "No cap on Pro and City Partner plans. Starter has a soft volume guideline tied to engagement quality." },
          ].map((f, i) => (
            <details key={i} className="border-b border-[var(--adn-line)] pb-5 group">
              <summary className="font-display text-lg font-semibold cursor-pointer list-none flex items-start justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-[var(--adn-gold)] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[var(--adn-text-soft)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
